import { t } from 'i18next'
import { Badge } from 'primereact/badge'
import { Tooltip } from 'primereact/tooltip'
import React from 'react'
import { getGradeValue } from './getGradeValue'
import {
  ApprenticeModuleGrade,
  Grade,
  GradeSchema,
} from '../../../../model/apprenticeModuleGrade.model'
import { ApprenticeshipModule } from '../../../../model/apprenticeshipModule.model'
import { findApprenticeshipModuleById } from '../../../../util/arraySearch'
import {
  getTranslatedGrade,
  gradeValue,
} from '../../../../util/getTranslatedGrade'

export const getModuleKey = (
  amg: ApprenticeModuleGrade,
  apprenticeshipModules: ApprenticeshipModule[]
) => {
  const module = findApprenticeshipModuleById(
    amg?.id_apprenticeship_module,
    apprenticeshipModules
  )
  if (module) {
    return module.module_key
  }
  return '?'
}

export const moduleKey = (
  amg: ApprenticeModuleGrade,
  apprenticeshipModules: ApprenticeshipModule[]
) => {
  const module = findApprenticeshipModuleById(
    amg?.id_apprenticeship_module,
    apprenticeshipModules
  )
  if (module) {
    return (
      <b className={'module-key flex w-full justify-content-center'}>
        {module.module_key}
      </b>
    )
  }
  return (
    <div className={'module-key flex w-full justify-content-center'}>?</div>
  )
}

export const getModuleName = (
  amg: ApprenticeModuleGrade | undefined,
  apprenticeshipModules: ApprenticeshipModule[]
) => {
  if (!amg) {
    return '?'
  }
  const module = findApprenticeshipModuleById(
    amg.id_apprenticeship_module,
    apprenticeshipModules
  )
  if (module?.description) {
    return module.description
  }
  return '?'
}

export const moduleName = (
  amg: ApprenticeModuleGrade | undefined,
  apprenticeshipModules: ApprenticeshipModule[]
) => {
  return (
    <span className={'secondary-text'}>
      {getModuleName(amg, apprenticeshipModules)}
    </span>
  )
}

export const roundingTypeBody = (amg: ApprenticeModuleGrade) => {
  return (
    <span className={'secondary-text small-text'}>
      {t<string>(
        `grade_module.grade.rounding_type.${amg.module_grade.grade_container.id_rounding_type}`
      )}
    </span>
  )
}

const getDiff = (numA: number, numB: number): number => {
  return numA - numB
}

const bestGradeIsHighestDigit = (gradeSchema: GradeSchema) => {
  if (
    !gradeSchema?.best_grade_value?.value ||
    !gradeSchema.worst_grade_value?.value
  ) {
    return undefined
  }
  return (
    gradeSchema.best_grade_value.value > gradeSchema.worst_grade_value.value
  )
}

export const getColorClass = (
  value: string | undefined | null,
  gradeSchema: GradeSchema | undefined
): string => {
  let colorClass: string = ''
  if (!value || !gradeSchema) {
    return ''
  }

  //passed failed only
  if (gradeSchema.grade_schema_id === 5) {
    return value === 'passed' ? 'tag-A' : 'tag-D'
  }

  const lowestGrade = gradeSchema.worst_grade_value?.value
  const highestGrade = gradeSchema.best_grade_value?.value
  const minPassedValue = gradeSchema.min_grade_value_passed?.value
  if (!lowestGrade || !highestGrade || !minPassedValue) {
    return ''
  }

  const actualGrade = parseFloat(value)
  const lowestGradeInSchema = parseFloat(lowestGrade)
  const highestGradeInSchema = parseFloat(highestGrade)
  const minPassedValueInSchema = parseFloat(minPassedValue)
  if (
    !actualGrade ||
    !lowestGradeInSchema ||
    !highestGradeInSchema ||
    !minPassedValueInSchema
  ) {
    //todo we only handle number values
    return ''
  }

  const highestValueInSchemaIsBestGrade = bestGradeIsHighestDigit(gradeSchema)

  // the schema says that the lowest grade is the worst and the highest the best grade
  if (highestValueInSchemaIsBestGrade) {
    // has passed
    if (actualGrade >= minPassedValueInSchema) {
      const diffToHighest = getDiff(highestGradeInSchema, actualGrade)
      const diffToMinPassed = getDiff(actualGrade, minPassedValueInSchema)
      if (diffToHighest >= diffToMinPassed) {
        colorClass = 'tag-B'
      } else {
        colorClass = 'tag-A'
      }
    }
    // has not passed
    else if (actualGrade < minPassedValueInSchema) {
      const diffToMinPassed = getDiff(minPassedValueInSchema, actualGrade)
      const diffToWorst = getDiff(actualGrade, lowestGradeInSchema)
      if (diffToWorst >= diffToMinPassed) {
        colorClass = 'tag-C'
      } else {
        colorClass = 'tag-D'
      }
    }
  }
  // e.g. german schema, lowest is best grade, highest is worst grade
  else if (!highestValueInSchemaIsBestGrade) {
    if (actualGrade <= minPassedValueInSchema) {
      const diffToHighest = getDiff(actualGrade, highestGradeInSchema)
      const diffToMinPassed = getDiff(minPassedValueInSchema, actualGrade)
      if (diffToHighest >= diffToMinPassed) {
        colorClass = 'tag-B'
      } else {
        colorClass = 'tag-A'
      }
    }
    // has not passed, the actual grade is higher than the min passed grade which is bad in this schema
    else if (actualGrade > minPassedValueInSchema) {
      const diffToMinPassed = getDiff(actualGrade, minPassedValueInSchema)
      const diffToWorst = getDiff(lowestGradeInSchema, actualGrade)
      if (diffToWorst >= diffToMinPassed) {
        colorClass = 'tag-C'
      } else {
        colorClass = 'tag-D'
      }
    }
  }
  return colorClass
}

export const getGradeBadge = (
  grade: ApprenticeModuleGrade | undefined | null,
  gS: GradeSchema[]
) => {
  if (!grade) {
    return <>-</>
  }
  let translatedGrade = getTranslatedGrade(grade)
  const gradeVal = grade?.module_grade?.grade_value
  const id = grade.apprentice_module_grade_id
  const schema = gS.find(
    (s) => s.grade_schema_id === grade.module_grade.id_grade_schema
  )
  const colorClass = getColorClass(gradeVal, schema)
  if (!gradeVal) {
    return translatedGrade
  }
  return (
    <Badge
      key={`badge_key_${id}`}
      style={{ minWidth: '40px', fontWeight: 600 }}
      className={colorClass}
      value={translatedGrade}
    />
  )
}

export const singleGradesBadges = (
  mGrade: ApprenticeModuleGrade,
  gradeSchemas: GradeSchema[]
) => {
  const values: JSX.Element[] = []
  const gradeSchemaId = mGrade?.module_grade?.id_grade_schema
  const schema = gradeSchemas.find((s) => s.grade_schema_id === gradeSchemaId)
  const mGradeId = mGrade?.apprentice_module_grade_id
  const grades = mGrade?.module_grade?.grade_container?.grades
  if (!grades || !grades.length) {
    return <></>
  }

  for (let i = 0; i < grades.length; i++) {
    const grade: Grade = grades[i]
    if (grade.is_active !== 1) {
      continue
    }

    let translatedGradeValue = gradeValue(grade.value)
    const colorClass = getColorClass(grade.value, schema)
    if (grade.counts_for_calculation === 0) {
      values.push(
        <div key={`sg_${mGradeId}-${i}`}>
          <span
            style={{ fontWeight: 600 }}
            className={`p-badge p-component dark-grey tt-grade-${grade.grade_id}`}
          >
            {translatedGradeValue}
          </span>
          <Tooltip
            target={`.tt-grade-${grade.grade_id}`}
            content={t<string>('grade_module.grade.counts_not_for_calculation')}
            showDelay={1000}
            position={'top'}
          />
        </div>
      )
    } else {
      let weight = parseFloat(String(grade.weight_in_grade_container))
      let diffWeight = 100 - weight
      if (grade?.weight_in_grade_container) {
        if (weight === 100) {
          values.push(
            <span
              style={{ fontWeight: 600 }}
              key={`sg_${mGradeId}-${i}`}
              className={`p-badge p-component ${colorClass}`}
            >
              {translatedGradeValue}
            </span>
          )
          //counts_not_for_calculation
        } else {
          values.push(
            <div key={`sg_${mGradeId}-${i}`}>
              <span
                style={{
                  backgroundImage: ` linear-gradient(to right, transparent ${weight}% ,#194154 ${diffWeight}%)`,
                  fontWeight: 600,
                }}
                className={` p-badge p-component  ${colorClass} tt-grade-${grade.grade_id}`}
              >
                {translatedGradeValue}
              </span>
              <Tooltip
                target={`.tt-grade-${grade.grade_id}`}
                content={`${weight}%`}
                showDelay={1000}
                position={'top'}
              />
            </div>
          )
        }
      }
    }
  }
  let uniqueVals = [...new Set(values)]
  // sometimes its buggy
  return <div className={'flex flex-wrap gap-1'}>{uniqueVals}</div>
}

export const singleGradesBadgesWithControlledFlag = (
  mGrade: ApprenticeModuleGrade,
  gradeSchemas: GradeSchema[]
) => {
  const values: JSX.Element[] = []
  const gradeSchemaId = mGrade?.module_grade?.id_grade_schema
  const schema = gradeSchemas.find((s) => s.grade_schema_id === gradeSchemaId)
  const mGradeId = mGrade?.apprentice_module_grade_id
  const grades = mGrade?.module_grade?.grade_container?.grades
  if (!grades || !grades.length) {
    return <></>
  }

  for (let i = 0; i < grades.length; i++) {
    const grade: Grade = grades[i]
    if (grade.is_active !== 1) {
      continue
    }

    let translatedGradeValue = gradeValue(grade.value)
    const colorClass = getColorClass(grade.value, schema)
    if (grade.counts_for_calculation === 0) {
      values.push(
        <div key={`sg_${mGradeId}-${i}`}>
          <span
            style={{ fontWeight: 600 }}
            className={`p-badge p-component dark-grey tt-grade-${grade.grade_id}`}
          >
            {translatedGradeValue}
          </span>
          <Tooltip
            target={`.tt-grade-${grade.grade_id}`}
            content={t<string>('grade_module.grade.counts_not_for_calculation')}
            showDelay={1000}
            position={'top'}
          />
        </div>
      )
    } else {
      let weight = parseFloat(String(grade.weight_in_grade_container))
      let diffWeight = 100 - weight
      if (grade?.weight_in_grade_container) {
        if (weight === 100) {
          values.push(
            <span
              style={{ fontWeight: 600 }}
              key={`sg_${mGradeId}-${i}`}
              className={`inline-flex align-items-center gap-1 p-badge p-component ${colorClass}`}
            >
              <span>{translatedGradeValue}</span>
              <span
                className={
                  grade.is_approved
                    ? ' fa-solid fa-check'
                    : ' fa-solid fa-times'
                }
              />
            </span>
          )
          //counts_not_for_calculation
        } else {
          values.push(
            <div key={`sg_${mGradeId}-${i}`}>
              <span
                style={{
                  backgroundImage: ` linear-gradient(to right, transparent ${weight}% ,#194154 ${diffWeight}%)`,
                  fontWeight: 600,
                }}
                className={`inline-flex align-items-center gap-1 
                p-badge p-component ${colorClass} tt-grade-${grade.grade_id}`}
              >
                <span>{translatedGradeValue}</span>
                <span
                  className={
                    grade.is_approved
                      ? ' fa-solid fa-check'
                      : ' fa-solid fa-times'
                  }
                />
              </span>
              <Tooltip
                target={`.tt-grade-${grade.grade_id}`}
                content={`${weight}%`}
                showDelay={1000}
                position={'top'}
              />
            </div>
          )
        }
      }
    }
  }
  let uniqueVals = [...new Set(values)]
  // sometimes its buggy
  return <div className={'flex flex-wrap gap-1'}>{uniqueVals}</div>
}

export const singleGradeBadge = (grade: Grade, gradeSchemas: GradeSchema[]) => {
  const gradeSchemaId = grade.id_grade_schema
  const schema = gradeSchemas.find((s) => s.grade_schema_id === gradeSchemaId)
  let translatedGradeValue = gradeValue(grade.value)
  const colorClass = getColorClass(grade.value, schema)

  // does not count for calculation
  if (grade.counts_for_calculation === 0) {
    return (
      <Badge
        style={{ fontWeight: '600' }}
        className={'dark-grey'}
        value={translatedGradeValue}
      />
    )
  } else {
    if (grade?.weight_in_grade_container) {
      return (
        <Badge
          style={{ fontWeight: '600' }}
          className={colorClass}
          value={translatedGradeValue}
        />
      )
    }
  }
}

export const singleGradeList = (rowData: ApprenticeModuleGrade) => {
  let val = ''
  // eslint-disable-next-line array-callback-return
  rowData?.module_grade?.grade_container?.grades?.map((gc: any) => {
    if (gc?.is_active === 1 && gc?.counts_for_calculation === 1) {
      if (val.length === 0) {
        val += `${getGradeValue(gc.value)} (${gc.weight_in_grade_container}%)`
      } else {
        val += `, ${getGradeValue(gc.value)} (${gc.weight_in_grade_container}%)`
      }
    }
  })
  return <span className={'secondary-text small-text'}>{val}</span>
}